import {
  cardStyle,
  ghostButtonStyle,
  inputStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  SectionTitle,
} from "../dashboardStyles";
import {
  formatCapturedAt,
  formatCurrency,
  getPermissionBadgeStyle,
  getPermissionLabel,
} from "../dashboardUtils";

function MiniCard({ title, value, helper }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 18,
        border: "1px solid #fed7aa",
        background: "#fffdf8",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 800, color: "#9a3412", textTransform: "uppercase" }}>
        {title}
      </div>
      <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: "#ea580c" }}>{value}</div>
      {helper ? <div style={{ marginTop: 6, color: "#7c2d12" }}>{helper}</div> : null}
    </div>
  );
}

export default function SettingsPage({
  activeDiscoveryRadiusLabel,
  activeDiscoveryRadiusSecondaryLabel,
  activateSubscription,
  adminDashboard,
  analyticsOverview,
  alerts,
  billingOverview,
  deleteAccount,
  deleteAccountForm,
  defaultAddressId,
  defaultDiscoveryRadiusLabel,
  deleteSavedAddress,
  deviceTokens,
  distanceUnitLabel = "km",
  favorites,
  isActivatingSubscription,
  isDeletingAccount,
  isLocating,
  isRequestingNotifications,
  isSavingSettings,
  markNotificationRead,
  newAddressLabel,
  notifications,
  shoppingListAlerts,
  removeAlert,
  removeShoppingListAlert,
  removeFavorite,
  locationPermission,
  logout,
  notificationPermission,
  profileForm,
  refreshPermissionStates,
  requestCurrentLocation,
  requestNotificationAccess,
  saveCurrentAddress,
  saveUserSettings,
  setDefaultAddress,
  setNewAddressLabel,
  secureContextAvailable,
  selectedAddressLabel,
  suspiciousPrices,
  language,
  languageOptions,
  setDeleteAccountValue,
  setLanguage,
  setProfileValue,
  t,
  userProfile,
  savedAddresses,
}) {
  return (
    <div className="settings-page-shell" style={{ display: "grid", gap: 20 }}>
      <div
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.94fr) minmax(0, 1.06fr)", gap: 20 }}
        className="settings-page-grid stack-on-mobile"
      >
        <div style={{ ...cardStyle, minWidth: 0 }}>
          <SectionTitle
            eyebrow={t?.("settings.account.eyebrow", "Conta") || "Conta"}
            title={
              userProfile?.full_name ||
              userProfile?.email ||
              (t?.("settings.account.title", "Configuracoes do usuario") || "Configuracoes do usuario")
            }
            description={
              t?.(
                "settings.account.description",
                "Ajuste idioma, localizacao base e raio de descoberta em um unico lugar."
              ) || "Ajuste idioma, localizacao base e raio de descoberta em um unico lugar."
            }
          />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <MiniCard
              title={t?.("settings.account.radius_short", "Raio ativo") || "Raio ativo"}
              value={activeDiscoveryRadiusLabel}
              helper={activeDiscoveryRadiusSecondaryLabel}
            />
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <div>
              <div style={{ marginBottom: 8, fontWeight: 700 }}>
                {t?.("auth.name", "Nome") || "Nome"}
              </div>
              <input
                style={inputStyle}
                value={profileForm.full_name}
                onChange={(event) => setProfileValue("full_name", event.target.value)}
                placeholder={t?.("settings.account.name_placeholder", "Como quer aparecer no app") || "Como quer aparecer no app"}
              />
            </div>

            <div>
              <div style={{ marginBottom: 8, fontWeight: 700 }}>
                {t?.("settings.language.label", "Idioma do app") || "Idioma do app"}
              </div>
              <select
                style={inputStyle}
                value={language}
                onChange={(event) => {
                  const nextLanguage = event.target.value;
                  setProfileValue("preferred_language", nextLanguage);
                  setLanguage(nextLanguage);
                }}
              >
                {(languageOptions || []).map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div style={{ marginTop: 8, color: "#9a3412" }}>
                {t?.(
                  "settings.language.help",
                  "Escolha o idioma da interface. Conteudos enviados por usuarios permanecem no texto original."
                ) || "Escolha o idioma da interface. Conteudos enviados por usuarios permanecem no texto original."}
              </div>
            </div>

            <div>
              <div style={{ marginBottom: 8, fontWeight: 700 }}>
                {t?.("settings.account.radius", `Raio de descoberta (${distanceUnitLabel})`) ||
                  `Raio de descoberta (${distanceUnitLabel})`}
              </div>
              <input
                style={inputStyle}
                type="number"
                min="1"
                max="250"
                step="1"
                inputMode="decimal"
                value={profileForm.discovery_radius_miles}
                onChange={(event) =>
                  setProfileValue("discovery_radius_miles", event.target.value)
                }
                placeholder={defaultDiscoveryRadiusLabel}
              />
              <div style={{ marginTop: 8, color: "#9a3412" }}>
                {(
                  t?.(
                    "settings.account.radius_help",
                    "Padrao recomendado: {{defaultRadius}}. Raio ativo agora: {{activeRadius}} | alternativo: {{secondaryRadius}}."
                  ) ||
                  "Padrao recomendado: {{defaultRadius}}. Raio ativo agora: {{activeRadius}} | alternativo: {{secondaryRadius}}."
                )
                  .replace("{{defaultRadius}}", defaultDiscoveryRadiusLabel)
                  .replace("{{activeRadius}}", activeDiscoveryRadiusLabel)
                  .replace("{{secondaryRadius}}", activeDiscoveryRadiusSecondaryLabel)}
              </div>
            </div>

            {selectedAddressLabel && (
              <div
                style={{
                  padding: 14,
                  borderRadius: 18,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#7c2d12",
                  fontWeight: 700,
                }}
              >
                {(t?.("settings.account.address_active", "Endereco ativo") || "Endereco ativo") + ": "}
                {selectedAddressLabel}
              </div>
            )}

            <div className="settings-action-row" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
              <button
                onClick={() => requestCurrentLocation({ persistProfile: true })}
                style={primaryButtonStyle}
                disabled={isLocating}
              >
                {isLocating
                  ? t?.("settings.account.updating_location", "Atualizando localizacao...") || "Atualizando localizacao..."
                  : t?.("settings.account.use_current", "Usar localizacao atual") || "Usar localizacao atual"}
              </button>
              <button
                onClick={saveUserSettings}
                style={secondaryButtonStyle}
                disabled={isSavingSettings}
              >
                {isSavingSettings
                  ? t?.("settings.account.saving", "Salvando...") || "Salvando..."
                  : t?.("settings.account.save_settings", "Salvar configuracoes") || "Salvar configuracoes"}
              </button>
            </div>
          </div>
        </div>

        <div className="settings-page-column" style={{ display: "grid", gap: 20 }}>
          <div style={{ ...cardStyle, minWidth: 0 }}>
            <SectionTitle
              eyebrow={t?.("billing.section.eyebrow", "Assinatura") || "Assinatura"}
              title={t?.("billing.section.title", "Plano do app") || "Plano do app"}
              description={
                t?.(
                  "billing.section.description",
                  "Todo usuario recebe 14 dias gratis. Depois, o acesso continua com a assinatura mensal na moeda local."
                ) ||
                "Todo usuario recebe 14 dias gratis. Depois, o acesso continua com a assinatura mensal na moeda local."
              }
            />

            {billingOverview ? (
              <div style={{ display: "grid", gap: 12 }}>
                <MiniCard
                  title={t?.("billing.section.status", "Status") || "Status"}
                  value={
                    billingOverview.is_active_subscription
                      ? t?.("billing.status.active", "Ativa") || "Ativa"
                      : billingOverview.is_trial_active
                        ? t?.("billing.status.trial", "Trial") || "Trial"
                        : t?.("billing.status.expired", "Pagamento pendente") || "Pagamento pendente"
                  }
                  helper={
                    billingOverview.is_trial_active
                      ? t?.("billing.section.trial_until", "Trial ate {{date}}", {
                          date: formatCapturedAt(billingOverview.trial_ends_at),
                        }) || `Trial ate ${formatCapturedAt(billingOverview.trial_ends_at)}`
                      : billingOverview.current_period_ends_at
                        ? t?.("billing.section.renews_until", "Ciclo atual ate {{date}}", {
                            date: formatCapturedAt(billingOverview.current_period_ends_at),
                          }) || `Ciclo atual ate ${formatCapturedAt(billingOverview.current_period_ends_at)}`
                        : t?.("billing.section.payment_required", "A assinatura precisa ser ativada para continuar.") ||
                          "A assinatura precisa ser ativada para continuar."
                  }
                />
                <MiniCard
                  title={t?.("billing.section.monthly_price", "Mensalidade") || "Mensalidade"}
                  value={formatCurrency(
                    billingOverview.price_preview?.monthly_price_local,
                    billingOverview.price_preview?.currency_code
                  )}
                  helper={t?.("billing.section.usd_reference", "Base USD: {{value}}", {
                    value: formatCurrency(
                      billingOverview.price_preview?.monthly_price_usd,
                      "USD"
                    ),
                  }) || `Base USD: ${formatCurrency(billingOverview.price_preview?.monthly_price_usd, "USD")}`}
                />
                <MiniCard
                  title={t?.("billing.section.country", "Pais e moeda") || "Pais e moeda"}
                  value={`${billingOverview.price_preview?.country_code || "US"} / ${billingOverview.price_preview?.currency_code || "USD"}`}
                  helper={t?.("billing.section.days_remaining", "{{count}} dia(s) restantes", {
                    count: billingOverview.days_remaining ?? 0,
                  }) || `${billingOverview.days_remaining ?? 0} dia(s) restantes`}
                />

                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {!billingOverview.access_granted ? (
                    <button
                      onClick={activateSubscription}
                      style={primaryButtonStyle}
                      disabled={isActivatingSubscription}
                    >
                      {isActivatingSubscription
                        ? t?.("billing.section.activating", "Ativando...") || "Ativando..."
                        : t?.("billing.section.activate", "Ativar assinatura") || "Ativar assinatura"}
                    </button>
                  ) : null}
                  <div style={{ color: "#9a3412", alignSelf: "center" }}>
                    {billingOverview.sandbox_mode
                      ? t?.(
                          "billing.section.sandbox",
                          "Sandbox ligado: em dev, a ativacao funciona como teste."
                        ) || "Sandbox ligado: em dev, a ativacao funciona como teste."
                      : t?.(
                          "billing.section.production_note",
                          "Em producao, conecte Google Play Billing e App Store."
                        ) || "Em producao, conecte Google Play Billing e App Store."}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: "#9a3412" }}>
                {t?.("billing.section.loading", "Carregando status da assinatura...") ||
                  "Carregando status da assinatura..."}
              </div>
            )}
          </div>

          <div style={{ ...cardStyle, minWidth: 0 }}>
            <SectionTitle
              eyebrow={t?.("settings.permissions.eyebrow", "Permissoes") || "Permissoes"}
              title={t?.("settings.permissions.title", "Dispositivo") || "Dispositivo"}
              description={
                t?.(
                  "settings.permissions.description",
                  "Ative localizacao e notificacoes para receber recomendacoes em tempo real."
                ) || "Ative localizacao e notificacoes para receber recomendacoes em tempo real."
              }
            />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  padding: 16,
                  borderRadius: 20,
                  background: "#ffffff",
                  border: "1px solid #fed7aa",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <strong>{t?.("settings.permissions.location", "Localizacao") || "Localizacao"}</strong>
                  <span style={getPermissionBadgeStyle(locationPermission)}>
                    {getPermissionLabel(locationPermission)}
                  </span>
                </div>
              </div>
              <div
                style={{
                  padding: 16,
                  borderRadius: 20,
                  background: "#ffffff",
                  border: "1px solid #fed7aa",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <strong>{t?.("settings.notifications.eyebrow", "Notificacoes") || "Notificacoes"}</strong>
                  <span style={getPermissionBadgeStyle(notificationPermission)}>
                    {getPermissionLabel(notificationPermission)}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 16 }}>
              <button
                onClick={requestNotificationAccess}
                style={ghostButtonStyle}
                disabled={isRequestingNotifications}
              >
                {isRequestingNotifications
                  ? t?.("settings.permissions.requesting", "Solicitando...") || "Solicitando..."
                  : t?.("settings.permissions.request", "Pedir notificacoes") || "Pedir notificacoes"}
              </button>
              <button onClick={refreshPermissionStates} style={ghostButtonStyle}>
                {t?.("settings.permissions.revalidate", "Revalidar permissoes") || "Revalidar permissoes"}
              </button>
            </div>

            {deviceTokens?.length ? (
              <div style={{ marginTop: 14, color: "#9a3412" }}>
                {(t?.("settings.permissions.devices_ready", "{{count}} dispositivo(s) pronto(s) para alertas", {
                  count: deviceTokens.length,
                }) || `${deviceTokens.length} dispositivo(s) pronto(s) para alertas`)}
              </div>
            ) : null}

            {!secureContextAvailable && (
              <div style={{ marginTop: 14, color: "#b45309" }}>
                {t?.("settings.permissions.http_warning", "Em celulares, geolocalizacao e notificacoes podem ser limitadas quando o app abre em HTTP pelo IP local.") || "Em celulares, geolocalizacao e notificacoes podem ser limitadas quando o app abre em HTTP pelo IP local."}
              </div>
            )}
          </div>

          <div style={{ ...cardStyle, minWidth: 0 }}>
            <SectionTitle
              eyebrow={t?.("settings.addresses.eyebrow", "Enderecos") || "Enderecos"}
              title={t?.("settings.addresses.title", "Pontos salvos") || "Pontos salvos"}
              description={
                t?.("settings.addresses.description", "Guarde casa, trabalho e outros pontos para trocar de base rapidamente.") ||
                "Guarde casa, trabalho e outros pontos para trocar de base rapidamente."
              }
            />

            <div style={{ display: "grid", gap: 12 }}>
              <input
                style={inputStyle}
                value={newAddressLabel}
                onChange={(event) => setNewAddressLabel(event.target.value)}
                placeholder={t?.("settings.addresses.label_placeholder", "Label do endereco: Casa, Trabalho, Praia...") || "Label do endereco: Casa, Trabalho, Praia..."}
              />
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button onClick={saveCurrentAddress} style={primaryButtonStyle}>
                  {t?.("settings.addresses.save_current", "Salvar endereco atual") || "Salvar endereco atual"}
                </button>
              </div>
              <div style={{ display: "grid", gap: 10 }}>
                {savedAddresses?.length > 0 ? (
                  savedAddresses.slice(0, 4).map((address) => (
                    <div
                      key={address.id}
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        border: "1px solid #fed7aa",
                        background: address.id === defaultAddressId ? "#fff7ed" : "#ffffff",
                      }}
                    >
                      <strong>{address.label}</strong>
                      <div style={{ marginTop: 6, color: "#7c2d12" }}>{address.address}</div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                        <button onClick={() => setDefaultAddress(address.id)} style={ghostButtonStyle}>
                          {address.id === defaultAddressId
                            ? t?.("settings.addresses.default", "Endereco padrao") || "Endereco padrao"
                            : t?.("settings.addresses.set_default", "Definir como padrao") || "Definir como padrao"}
                        </button>
                        <button onClick={() => deleteSavedAddress(address.id)} style={ghostButtonStyle}>
                          {t?.("settings.favorites.remove", "Remover") || "Remover"}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#9a3412" }}>
                    {t?.("settings.addresses.empty", "Salve o endereco atual para criar seus pontos favoritos de busca.") ||
                      "Salve o endereco atual para criar seus pontos favoritos de busca."}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ ...cardStyle, minWidth: 0 }}>
            <SectionTitle
              eyebrow={t?.("settings.session.eyebrow", "Sessao") || "Sessao"}
              title={t?.("settings.session.title", "Sessao e conta") || "Sessao e conta"}
              description={t?.("settings.session.description", "Saia deste dispositivo ou exclua sua conta de forma definitiva.") || "Saia deste dispositivo ou exclua sua conta de forma definitiva."}
            />
            <div style={{ display: "grid", gap: 14 }}>
              <button onClick={logout} style={secondaryButtonStyle}>
                {t?.("settings.session.logout", "Sair da conta") || "Sair da conta"}
              </button>

              <div
                style={{
                  padding: 16,
                  borderRadius: 18,
                  border: "1px solid #fecaca",
                  background: "#fff7f7",
                  display: "grid",
                  gap: 12,
                }}
              >
                <strong style={{ color: "#9f1239" }}>
                  {t?.("settings.session.delete_title", "Excluir conta") || "Excluir conta"}
                </strong>
                <div style={{ color: "#881337" }}>
                  {t?.(
                    "settings.session.delete_description",
                    "Isso remove seu acesso, seus enderecos, favoritos, alertas e dispositivos conectados. Essa acao nao pode ser desfeita."
                  ) || "Isso remove seu acesso, seus enderecos, favoritos, alertas e dispositivos conectados. Essa acao nao pode ser desfeita."}
                </div>
                <input
                  style={inputStyle}
                  type="password"
                  value={deleteAccountForm.current_password}
                  onChange={(event) => setDeleteAccountValue("current_password", event.target.value)}
                  placeholder={
                    t?.("settings.session.delete_password", "Sua senha atual") ||
                    "Sua senha atual"
                  }
                />
                <input
                  style={inputStyle}
                  value={deleteAccountForm.confirmation_text}
                  onChange={(event) => setDeleteAccountValue("confirmation_text", event.target.value)}
                  placeholder={
                    t?.("settings.session.delete_confirm", 'Digite DELETE para confirmar') ||
                    "Digite DELETE para confirmar"
                  }
                />
                <button
                  onClick={deleteAccount}
                  disabled={isDeletingAccount}
                  style={{
                    ...primaryButtonStyle,
                    background: isDeletingAccount ? "#fb7185" : "linear-gradient(135deg, #e11d48, #f97316)",
                    boxShadow: "0 18px 40px rgba(225, 29, 72, 0.26)",
                  }}
                >
                  {isDeletingAccount
                    ? t?.("settings.session.delete_loading", "Excluindo conta...") || "Excluindo conta..."
                    : t?.("settings.session.delete_button", "Excluir conta agora") || "Excluir conta agora"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))", gap: 20 }}
      >
        <div style={{ ...cardStyle, minWidth: 0 }}>
          <SectionTitle
            eyebrow={t?.("settings.alerts.eyebrow", "Alertas") || "Alertas"}
            title={t?.("settings.alerts.title", "Queda de preco") || "Queda de preco"}
            description={t?.("settings.alerts.description", "Aqui ficam os alertas ativos mais importantes.") || "Aqui ficam os alertas ativos mais importantes."}
          />
          <div style={{ display: "grid", gap: 10 }}>
            {alerts?.length > 0 ? (
              alerts.slice(0, 4).map((alert) => (
                <div key={alert.id} style={{ padding: 14, borderRadius: 18, border: "1px solid #fed7aa" }}>
                  <strong>
                    {alert.product_name ||
                      t?.("settings.alerts.product_fallback", "Produto #{{id}}", {
                        id: alert.product_id,
                      }) ||
                      `Produto #${alert.product_id}`}
                  </strong>
                  <div style={{ marginTop: 6, color: "#7c2d12" }}>
                    {(t?.("settings.alerts.goal", "Meta") || "Meta") + ": "}
                    {formatCurrency(alert.target_price)}
                  </div>
                  <button onClick={() => removeAlert(alert.id)} style={{ ...ghostButtonStyle, marginTop: 12 }}>
                    {t?.("settings.alerts.remove", "Remover alerta") || "Remover alerta"}
                  </button>
                </div>
              ))
            ) : (
              <div style={{ color: "#9a3412" }}>
                {t?.("settings.alerts.empty", "Nenhum alerta ativo ainda.") || "Nenhum alerta ativo ainda."}
              </div>
            )}
          </div>
        </div>

        <div style={{ ...cardStyle, minWidth: 0 }}>
          <SectionTitle
            eyebrow={t?.("settings.notifications.eyebrow", "Notificacoes") || "Notificacoes"}
            title={t?.("settings.notifications.title", "Central de avisos") || "Central de avisos"}
            description={t?.("settings.notifications.description", "Veja so os avisos mais recentes.") || "Veja so os avisos mais recentes."}
          />
          <div style={{ display: "grid", gap: 10 }}>
            {notifications?.length > 0 ? (
              notifications.slice(0, 4).map((notification) => (
                <div
                  key={notification.id}
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    border: "1px solid #fed7aa",
                    background: notification.is_read ? "#ffffff" : "#fff7ed",
                  }}
                >
                  <strong>{notification.title}</strong>
                  <div style={{ marginTop: 6, color: "#7c2d12" }}>{notification.body}</div>
                  {!notification.is_read && (
                    <button
                      onClick={() => markNotificationRead(notification.id)}
                      style={{ ...ghostButtonStyle, marginTop: 12 }}
                    >
                      {t?.("settings.notifications.mark_read", "Marcar como lida") || "Marcar como lida"}
                    </button>
                  )}
                </div>
              ))
            ) : (
              <div style={{ color: "#9a3412" }}>
                {t?.("settings.notifications.empty", "Nenhuma notificacao por enquanto.") ||
                  "Nenhuma notificacao por enquanto."}
              </div>
            )}
          </div>
        </div>
      </div>

      {userProfile?.is_admin && (
        <div style={{ display: "grid", gap: 20, minWidth: 0 }}>
          <div style={{ ...cardStyle, minWidth: 0 }}>
            <SectionTitle
              eyebrow={t?.("settings.admin.eyebrow", "Painel admin") || "Painel admin"}
              title={t?.("settings.admin.title", "Mercados, contribuicoes e precos suspeitos") || "Mercados, contribuicoes e precos suspeitos"}
              description={t?.("settings.admin.description", "Resumo rapido da operacao.") || "Resumo rapido da operacao."}
            />
            {adminDashboard ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                <MiniCard title={t?.("settings.admin.markets", "Mercados") || "Mercados"} value={adminDashboard.total_markets} />
                <MiniCard title={t?.("settings.admin.products", "Produtos") || "Produtos"} value={adminDashboard.total_products} />
                <MiniCard title={t?.("settings.admin.contributions", "Contribuicoes") || "Contribuicoes"} value={adminDashboard.total_contributions} />
                <MiniCard title={t?.("settings.admin.suspicious", "Suspeitos") || "Suspeitos"} value={adminDashboard.suspicious_prices} />
              </div>
            ) : (
              <div style={{ color: "#9a3412" }}>
                {t?.("settings.admin.loading", "Painel carregando...") || "Painel carregando..."}
              </div>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
              gap: 20,
            }}
          >
            <div style={{ ...cardStyle, minWidth: 0 }}>
              <strong style={{ display: "block", marginBottom: 12 }}>
                {t?.("settings.analytics.title", "Analytics") || "Analytics"}
              </strong>
              {analyticsOverview ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div>
                    <strong>{t?.("settings.analytics.searches", "Buscas quentes") || "Buscas quentes"}</strong>
                    {(analyticsOverview.top_searches || []).slice(0, 4).map((item) => (
                      <div key={`search-${item.query}`} style={{ color: "#7c2d12", marginTop: 6 }}>
                        {item.query}: {item.count}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ color: "#9a3412" }}>
                  {t?.("settings.analytics.loading", "Analytics carregando...") || "Analytics carregando..."}
                </div>
              )}
            </div>

            <div style={{ ...cardStyle, minWidth: 0 }}>
              <strong style={{ display: "block", marginBottom: 12 }}>
                {t?.("settings.suspicious.title", "Precos suspeitos") || "Precos suspeitos"}
              </strong>
              <div style={{ display: "grid", gap: 10 }}>
                {suspiciousPrices?.length > 0 ? (
                  suspiciousPrices.slice(0, 4).map((item) => (
                    <div key={item.price_id} style={{ color: "#7c2d12" }}>
                      {t?.("settings.suspicious.item_line", "{{product}} em {{market}}: {{price}}", {
                        product: item.product,
                        market: item.market,
                        price: formatCurrency(item.price),
                      }) || `${item.product} em ${item.market}: ${formatCurrency(item.price)}`}
                    </div>
                  ))
                ) : (
                  <div style={{ color: "#9a3412" }}>
                    {t?.("settings.suspicious.empty", "Nenhum preco suspeito carregado.") || "Nenhum preco suspeito carregado."}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
